import React from 'react';
import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard, Users, Building2, Briefcase, BarChart3, Settings, Bell,
  CreditCard, Phone, FileText, Shield, DollarSign, TrendingUp, PieChart,
  Home, UserCog, Wallet, MessageSquare, Target, Activity, Search, Command,
  ClipboardCheck, Eye, BookOpen, History, Calendar, ArrowRightLeft, Globe,
  Lock, Percent, Archive, Scale, LineChart, Database, AlertTriangle
} from 'lucide-react';
import { Logo } from './index';

const SidebarLink = ({ to, icon: Icon, children, end = false }) => (
  <NavLink
    to={to}
    end={end}
    className={({ isActive }) => `sidebar-item ${isActive ? 'active' : ''}`}
  >
    <Icon className="w-5 h-5" />
    <span className="flex-1">{children}</span>
  </NavLink>
);

const SidebarSection = ({ title, children }) => (
  <div className="mb-6">
    <p className="sidebar-section-title">{title}</p>
    <div className="space-y-1">{children}</div>
  </div>
);

const PillarCaption = ({ text }) => (
  <div className="px-4 py-2 mb-2">
    <p className="text-[10px] font-mono uppercase tracking-[0.1em] text-gray-400 leading-tight">{text}</p>
  </div>
);

const SidebarSearch = () => (
  <div className="px-4 mb-6">
    <div className="relative">
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
      <input
        type="text"
        placeholder="Search"
        className="w-full pl-9 pr-10 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/20"
      />
      <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-0.5 text-gray-400">
        <Command className="w-3 h-3" />
        <span className="text-xs">F</span>
      </div>
    </div>
  </div>
);

// ============================================================
// SUPER ADMIN SIDEBAR
// Routes: /admin/*
// ============================================================
export const SuperAdminSidebar = () => (
  <aside className="w-64 bg-white border-r border-gray-100 h-screen overflow-y-auto flex flex-col">
    <div className="p-5 border-b border-gray-100">
      <Logo />
    </div>
    <SidebarSearch />
    <nav className="flex-1 px-3 space-y-1">
      <SidebarSection title="PLATFORM OVERVIEW">
        <SidebarLink to="/admin" icon={Home} end>Home/Overview</SidebarLink>
      </SidebarSection>
      <SidebarSection title="TENANT MANAGEMENT">
        <SidebarLink to="/admin/banks" icon={Building2}>Bank Management</SidebarLink>
        <SidebarLink to="/admin/investors" icon={Briefcase}>Investor Management</SidebarLink>
        <SidebarLink to="/admin/users" icon={Users}>User Management</SidebarLink>
        <SidebarLink to="/admin/demo-access" icon={Eye}>Demo Access</SidebarLink>
      </SidebarSection>
      <SidebarSection title="OPERATIONS">
        <SidebarLink to="/admin/transactions" icon={DollarSign}>Transaction Oversight</SidebarLink>
        <SidebarLink to="/admin/pnl" icon={TrendingUp}>Platform P&L</SidebarLink>
        <SidebarLink to="/admin/instruments" icon={CreditCard}>All Instruments</SidebarLink>
        <SidebarLink to="/admin/simulator" icon={LineChart}>Simulator Admin</SidebarLink>
        <SidebarLink to="/admin/live-simulation" icon={Activity}>Live Simulation</SidebarLink>
      </SidebarSection>
      <SidebarSection title="COMPLIANCE & RISK">
        <SidebarLink to="/admin/compliance" icon={Shield}>Compliance</SidebarLink>
        <SidebarLink to="/admin/kyc" icon={ClipboardCheck}>KYC & Onboarding</SidebarLink>
        <SidebarLink to="/admin/risk" icon={AlertTriangle}>Risk Management</SidebarLink>
      </SidebarSection>
      <SidebarSection title="ANALYTICS & REPORTING">
        <SidebarLink to="/admin/analytics" icon={BarChart3}>Analytics Dashboard</SidebarLink>
        <SidebarLink to="/admin/waitlist" icon={ClipboardCheck}>Waitlist</SidebarLink>
      </SidebarSection>
      <SidebarSection title="INTEGRATIONS">
        <SidebarLink to="/admin/csd-integration" icon={Database}>CSD Integration</SidebarLink>
        <SidebarLink to="/admin/api-integration" icon={Globe}>API Integration</SidebarLink>
      </SidebarSection>
      <SidebarSection title="ACCOUNT">
        <SidebarLink to="/admin/notifications" icon={Bell}>Notifications</SidebarLink>
        <SidebarLink to="/admin/settings" icon={Settings}>Settings</SidebarLink>
      </SidebarSection>
    </nav>
    <div className="p-4 border-t border-gray-100">
      <div className="flex items-center gap-2 text-xs text-gray-400">
        <div className="w-2 h-2 bg-red-500 rounded-full"></div>
        Platform Admin
      </div>
    </div>
  </aside>
);

// ============================================================
// BANK ADMIN SIDEBAR
// Routes: /bank/admin/*
// ============================================================
export const BankAdminSidebar = () => (
  <aside className="w-64 bg-white border-r border-gray-100 h-screen overflow-y-auto flex flex-col">
    <div className="p-5 border-b border-gray-100">
      <Logo />
    </div>
    <PillarCaption text="Compliance & liquidity administration" />
    <SidebarSearch />
    <nav className="flex-1 px-3 space-y-1">
      <SidebarSection title="ADMINISTRATION">
        <SidebarLink to="/bank/admin" icon={Home} end>Home/Overview</SidebarLink>
        <SidebarLink to="/bank/admin/users" icon={Users}>Users & Roles</SidebarLink>
      </SidebarSection>
      <SidebarSection title="CONFIGURATION">
        <SidebarLink to="/bank/admin/interfaces" icon={Settings}>Interfaces & Parameters</SidebarLink>
        <SidebarLink to="/bank/admin/profile" icon={Building2}>Bank Profile</SidebarLink>
      </SidebarSection>
      <SidebarSection title="SIMULATION">
        <SidebarLink to="/bank/admin/market-simulator" icon={LineChart}>Market Simulator</SidebarLink>
      </SidebarSection>
      <SidebarSection title="GOVERNANCE">
        <SidebarLink to="/bank/admin/audit" icon={History}>Audit</SidebarLink>
      </SidebarSection>
    </nav>
    <div className="p-4 border-t border-gray-100">
      <div className="flex items-center gap-2 text-xs text-gray-400">
        <div className="w-2 h-2 bg-green-500 rounded-full"></div>
        Bank Admin
      </div>
    </div>
  </aside>
);

// ============================================================
// COMPLIANCE MAKER SIDEBAR
// Routes: /bank/compliance-maker/*
// ============================================================
export const ComplianceMakerSidebar = () => (
  <aside className="w-64 bg-white border-r border-gray-100 h-screen overflow-y-auto flex flex-col">
    <div className="p-5 border-b border-gray-100">
      <Logo />
    </div>
    <PillarCaption text="Compliance fulfilment" />
    <SidebarSearch />
    <nav className="flex-1 px-3 space-y-1">
      <SidebarSection title="COMPLIANCE REVIEW">
        <SidebarLink to="/bank/compliance-maker" icon={ClipboardCheck} end>KYC Queue</SidebarLink>
        <SidebarLink to="/bank/compliance-maker/cds" icon={CreditCard}>CDS Signing</SidebarLink>
        <SidebarLink to="/bank/compliance-maker/screening" icon={Shield}>Screening</SidebarLink>
      </SidebarSection>
      <SidebarSection title="RECORDS">
        <SidebarLink to="/bank/compliance-maker/documents" icon={FileText}>Documents</SidebarLink>
      </SidebarSection>
      <SidebarSection title="GOVERNANCE">
        <SidebarLink to="/bank/compliance-maker/audit" icon={History}>Audit</SidebarLink>
      </SidebarSection>
    </nav>
    <div className="p-4 border-t border-gray-100">
      <div className="flex items-center gap-2 text-xs text-gray-400">
        <div className="w-2 h-2 bg-amber-500 rounded-full"></div>
        Compliance Maker
      </div>
    </div>
  </aside>
);

// ============================================================
// COMPLIANCE CHECKER SIDEBAR
// Routes: /bank/compliance-checker/*
// ============================================================
export const ComplianceCheckerSidebar = () => (
  <aside className="w-64 bg-white border-r border-gray-100 h-screen overflow-y-auto flex flex-col">
    <div className="p-5 border-b border-gray-100">
      <Logo />
    </div>
    <PillarCaption text="Compliance fulfilment" />
    <SidebarSearch />
    <nav className="flex-1 px-3 space-y-1">
      <SidebarSection title="COMPLIANCE CONFIRMATION">
        <SidebarLink to="/bank/compliance-checker" icon={ClipboardCheck} end>KYC Confirmation</SidebarLink>
        <SidebarLink to="/bank/compliance-checker/cds" icon={CreditCard}>CDS Signing</SidebarLink>
        <SidebarLink to="/bank/compliance-checker/screening" icon={Eye}>Screening</SidebarLink>
      </SidebarSection>
      <SidebarSection title="RECORDS">
        <SidebarLink to="/bank/compliance-checker/documents" icon={FileText}>Documents</SidebarLink>
        <SidebarLink to="/bank/compliance-checker/reviews" icon={Calendar}>Periodic Reviews</SidebarLink>
      </SidebarSection>
      <SidebarSection title="GOVERNANCE">
        <SidebarLink to="/bank/compliance-checker/audit" icon={History}>Audit</SidebarLink>
      </SidebarSection>
    </nav>
    <div className="p-4 border-t border-gray-100">
      <div className="flex items-center gap-2 text-xs text-gray-400">
        <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
        Compliance Checker
      </div>
    </div>
  </aside>
);

// ============================================================
// TREASURY DEALER SIDEBAR
// Routes: /bank/treasury/*
// ============================================================
export const TreasuryDealerSidebar = () => (
  <aside className="w-64 bg-white border-r border-gray-100 h-screen overflow-y-auto flex flex-col">
    <div className="p-5 border-b border-gray-100">
      <Logo />
    </div>
    <PillarCaption text="Exchange rates & execution supply" />
    <SidebarSearch />
    <nav className="flex-1 px-3 space-y-1">
      <SidebarSection title="QUOTING">
        <SidebarLink to="/bank/treasury" icon={ArrowRightLeft} end>Foreign Exchange Quotes</SidebarLink>
        <SidebarLink to="/bank/treasury/rules" icon={Activity}>Auto Rules</SidebarLink>
      </SidebarSection>
      <SidebarSection title="DEALING">
        <SidebarLink to="/bank/treasury/settlement" icon={Wallet}>Settlement</SidebarLink>
        <SidebarLink to="/bank/treasury/blotter" icon={BookOpen}>Blotter</SidebarLink>
      </SidebarSection>
      <SidebarSection title="RISK">
        <SidebarLink to="/bank/treasury/positions" icon={Scale}>Positions</SidebarLink>
      </SidebarSection>
      <SidebarSection title="REPORTING">
        <SidebarLink to="/bank/treasury/analytics" icon={BarChart3}>Analytics</SidebarLink>
        <SidebarLink to="/bank/treasury/compliance" icon={Lock}>Compliance & Audit</SidebarLink>
      </SidebarSection>
    </nav>
    <div className="p-4 border-t border-gray-100">
      <div className="flex items-center gap-2 text-xs text-gray-400">
        <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
        Treasury Dealer
      </div>
    </div>
  </aside>
);

// ============================================================
// FORWARDS DEALER SIDEBAR
// Routes: /bank/forwards/*
// ============================================================
export const ForwardsDealerSidebar = () => (
  <aside className="w-64 bg-white border-r border-gray-100 h-screen overflow-y-auto flex flex-col">
    <div className="p-5 border-b border-gray-100">
      <Logo />
    </div>
    <PillarCaption text="Hedging supply" />
    <SidebarSearch />
    <nav className="flex-1 px-3 space-y-1">
      <SidebarSection title="FORWARDS TRADING">
        <SidebarLink to="/bank/forwards" icon={TrendingUp} end>Forwards Quotes</SidebarLink>
        <SidebarLink to="/bank/forwards/settlement" icon={Wallet}>Settlement</SidebarLink>
        <SidebarLink to="/bank/forwards/contracts" icon={BookOpen}>Contracts Book</SidebarLink>
      </SidebarSection>
      <SidebarSection title="RISK">
        <SidebarLink to="/bank/forwards/positions" icon={Scale}>Positions</SidebarLink>
      </SidebarSection>
      <SidebarSection title="REPORTING">
        <SidebarLink to="/bank/forwards/analytics" icon={BarChart3}>Analytics</SidebarLink>
        <SidebarLink to="/bank/forwards/audit" icon={History}>Audit</SidebarLink>
      </SidebarSection>
    </nav>
    <div className="p-4 border-t border-gray-100">
      <div className="flex items-center gap-2 text-xs text-gray-400">
        <div className="w-2 h-2 bg-indigo-500 rounded-full"></div>
        Forwards Dealer
      </div>
    </div>
  </aside>
);

// ============================================================
// FAMILY OFFICE SIDEBAR
// Routes: /investor/admin/*
// Sections grouped by pillar (§5.1)
// ============================================================
export const FamilyOfficeSidebar = () => (
  <aside className="w-64 bg-white border-r border-gray-100 h-screen overflow-y-auto flex flex-col">
    <div className="p-5 border-b border-gray-100">
      <Logo />
    </div>
    <SidebarSearch />
    <nav className="flex-1 px-3 space-y-1">
      <SidebarSection title="HOME">
        <SidebarLink to="/investor/admin" icon={Home} end>Overview</SidebarLink>
      </SidebarSection>
      <SidebarSection title="1 · YIELD DISCOVERY">
        <SidebarLink to="/investor/admin/yield" icon={Target}>Yield Discovery</SidebarLink>
        <SidebarLink to="/simulator" icon={BarChart3}>Simulator</SidebarLink>
      </SidebarSection>
      <SidebarSection title="2 · EXCHANGE RATES">
        <SidebarLink to="/investor/admin/rates" icon={ArrowRightLeft}>Rates Board & FX RFQ</SidebarLink>
      </SidebarSection>
      <SidebarSection title="3 · COMPLIANCE">
        <SidebarLink to="/investor/admin/compliance" icon={ClipboardCheck}>Compliance</SidebarLink>
      </SidebarSection>
      <SidebarSection title="4 · HEDGING DEVICES">
        <SidebarLink to="/investor/admin/hedging" icon={Shield}>Hedging</SidebarLink>
      </SidebarSection>
      <SidebarSection title="5 · LIQUIDITY">
        <SidebarLink to="/investor/admin/liquidity" icon={Activity}>Liquidity</SidebarLink>
      </SidebarSection>
      <SidebarSection title="6 · EXECUTION">
        <SidebarLink to="/investor/admin/execution" icon={DollarSign}>Execution</SidebarLink>
      </SidebarSection>
      <SidebarSection title="7 · PORTFOLIO">
        <SidebarLink to="/investor/admin/portfolio" icon={Briefcase}>Portfolio</SidebarLink>
        <SidebarLink to="/investor/admin/projections" icon={LineChart}>Projections</SidebarLink>
      </SidebarSection>
      <SidebarSection title="ACCOUNT">
        <SidebarLink to="/investor/admin/profile" icon={Building2}>Profile</SidebarLink>
        <SidebarLink to="/investor/admin/messages" icon={MessageSquare}>Messages</SidebarLink>
      </SidebarSection>
    </nav>
    <div className="p-4 border-t border-gray-100">
      <div className="flex items-center gap-2 text-xs text-gray-400">
        <div className="w-2 h-2 bg-indigo-500 rounded-full"></div>
        Family Office
      </div>
    </div>
  </aside>
);

// ============================================================
// INDIVIDUAL INVESTOR SIDEBAR
// Routes: /investor/*
// Sections grouped by pillar (§5.1)
// ============================================================
export const IndividualInvestorSidebar = () => (
  <aside className="w-64 bg-white border-r border-gray-100 h-screen overflow-y-auto flex flex-col">
    <div className="p-5 border-b border-gray-100">
      <Logo />
    </div>
    <SidebarSearch />
    <nav className="flex-1 px-3 space-y-1">
      <SidebarSection title="HOME">
        <SidebarLink to="/investor" icon={Settings} end>Overview</SidebarLink>
      </SidebarSection>
      <SidebarSection title="1 · YIELD DISCOVERY">
        <SidebarLink to="/investor/yield" icon={Target}>Yield Discovery</SidebarLink>
        <SidebarLink to="/simulator" icon={BarChart3}>Simulator</SidebarLink>
      </SidebarSection>
      <SidebarSection title="2 · EXCHANGE RATES">
        <SidebarLink to="/investor/rates" icon={ArrowRightLeft}>Rates Board & FX RFQ</SidebarLink>
      </SidebarSection>
      <SidebarSection title="3 · COMPLIANCE">
        <SidebarLink to="/investor/compliance" icon={ClipboardCheck}>Compliance</SidebarLink>
      </SidebarSection>
      <SidebarSection title="4 · HEDGING DEVICES">
        <SidebarLink to="/investor/hedging" icon={Shield}>Hedging</SidebarLink>
      </SidebarSection>
      <SidebarSection title="5 · LIQUIDITY">
        <SidebarLink to="/investor/liquidity" icon={Activity}>Liquidity</SidebarLink>
      </SidebarSection>
      <SidebarSection title="6 · EXECUTION">
        <SidebarLink to="/investor/execution" icon={DollarSign}>Execution</SidebarLink>
      </SidebarSection>
      <SidebarSection title="7 · PORTFOLIO">
        <SidebarLink to="/investor/portfolio" icon={Briefcase}>Portfolio</SidebarLink>
        <SidebarLink to="/investor/projections" icon={LineChart}>Projections</SidebarLink>
      </SidebarSection>
      <SidebarSection title="ACCOUNT">
        <SidebarLink to="/investor/profile" icon={Building2}>Profile</SidebarLink>
        <SidebarLink to="/investor/messages" icon={MessageSquare}>Messages</SidebarLink>
      </SidebarSection>
    </nav>
    <div className="p-4 border-t border-gray-100">
      <div className="flex items-center gap-2 text-xs text-gray-400">
        <div className="w-2 h-2 bg-teal-500 rounded-full"></div>
        Individual Investor
      </div>
    </div>
  </aside>
);

const Sidebars = {
  SuperAdminSidebar,
  BankAdminSidebar,
  ComplianceMakerSidebar,
  ComplianceCheckerSidebar,
  TreasuryDealerSidebar,
  ForwardsDealerSidebar,
  FamilyOfficeSidebar,
  IndividualInvestorSidebar,
};
export default Sidebars;
